import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, X, ShieldCheck } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CloudImage } from "@/components/CloudImage";
import { GradeBadge } from "@/components/GradeBadge";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";
import { CLD_THUMB } from "@/lib/variants";

const EASE = [0.16, 1, 0.3, 1];

function LineThumb({ item }) {
  return (
    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-border bg-surface">
      {item.image ? (
        <CloudImage
          url={item.image}
          alt={item.name}
          transform={CLD_THUMB}
          className="absolute inset-0 h-full w-full object-contain"
        />
      ) : (
        <div className="placeholder-spot absolute inset-0" aria-hidden="true" />
      )}
    </div>
  );
}

export function CartDrawer() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, subtotal, count, drawerOpen, setDrawerOpen, closeDrawer } = useCart();

  const go = (to) => {
    closeDrawer();
    navigate(to);
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 border-line bg-ink p-0 shadow-2xl shadow-black/40 sm:max-w-[440px]"
        data-testid="mini-cart-drawer"
      >
        {/* Header */}
        <SheetHeader className="flex-row items-center justify-between space-y-0 border-b border-border px-5 py-4 text-left">
          <div className="flex items-baseline gap-3">
            <SheetTitle className="font-display text-lg font-semibold tracking-tightest text-foreground">Carrello</SheetTitle>
            <span className="font-mono text-[11px] uppercase tracking-mono text-muted-foreground" data-testid="mini-cart-count">
              {String(count).padStart(2, "0")} {count === 1 ? "articolo" : "articoli"}
            </span>
          </div>
          <SheetDescription className="sr-only">Riepilogo degli articoli nel carrello</SheetDescription>
          <button
            onClick={closeDrawer}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-200 hover:text-foreground"
            aria-label="Chiudi carrello"
            data-testid="mini-cart-close-button"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-start justify-center px-6" data-testid="mini-cart-empty">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border">
              <ShoppingBag className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            </span>
            <p className="mt-5 font-display text-xl font-semibold tracking-tightest text-foreground">Il carrello è vuoto</p>
            <p className="mt-2 text-sm text-muted-foreground">Quando aggiungi un prodotto lo trovi qui.</p>
            <Button variant="lime" className="mt-6" onClick={() => go("/catalogo")} data-testid="mini-cart-browse-button">
              Vai al catalogo <ArrowRight strokeWidth={2} />
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1">
              <ul className="divide-y divide-border px-5" data-testid="mini-cart-items">
                <AnimatePresence initial={false}>
                  {items.map((item) => (
                    <motion.li
                      key={item._lineId}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.24, ease: EASE }}
                      className="flex gap-4 py-4"
                      data-testid="mini-cart-item"
                    >
                      <LineThumb item={item} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-mono text-muted-foreground">{item.brand}</div>
                            <button
                              onClick={() => go(`/prodotto/${item.slug}`)}
                              className="mt-0.5 block truncate text-left text-sm font-medium text-foreground transition-colors hover:text-lime"
                              data-testid="mini-cart-item-name"
                            >
                              {item.name}
                            </button>
                            <div className="mt-1"><GradeBadge grade={item.grade} compact /></div>
                          </div>
                          <button
                            onClick={() => removeItem(item._lineId)}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-200 hover:text-destructive"
                            aria-label="Rimuovi"
                            data-testid="mini-cart-item-remove"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center rounded-full border border-border">
                            <button
                              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                              onClick={() => updateQty(item._lineId, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                              aria-label="Diminuisci quantità"
                              data-testid="mini-cart-qty-decrease"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-7 text-center font-mono text-sm" data-testid="mini-cart-item-qty">{item.quantity}</span>
                            <button
                              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                              onClick={() => updateQty(item._lineId, item.quantity + 1)}
                              aria-label="Aumenta quantità"
                              data-testid="mini-cart-qty-increase"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="font-mono text-sm font-medium text-foreground" data-testid="mini-cart-item-total">
                            {formatPrice(item.unit_price * item.quantity)}
                          </span>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            </ScrollArea>

            {/* Footer */}
            <div className="border-t border-border bg-surface/40 px-5 pb-6 pt-5">
              <div className="flex items-center justify-between">
                <span className="mono-label text-muted-foreground">Subtotale</span>
                <span className="font-mono text-xl font-medium text-foreground" data-testid="mini-cart-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-lime" strokeWidth={2} /> Spedizione gratuita · Reso 14 giorni
              </p>
              <Button variant="lime" size="lg" className="mt-4 w-full justify-between" onClick={() => go("/checkout")} data-testid="mini-cart-checkout-button">
                Vai al checkout <ArrowRight strokeWidth={2} />
              </Button>
              <Button variant="outline" className="mt-2.5 w-full" onClick={() => go("/carrello")} data-testid="mini-cart-view-cart-button">
                Vedi carrello
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
